import { z } from "zod/v4";

import { Option } from "api/common/types/common.types";
import {
  createZodNumberOption,
  createZodStringOption,
} from "components/Forms/utils/validation.utils";

import { PcmFileMetadata } from "./sections/PcmFileUploadSection";

import type { FeatureData } from "components/Forms/components/sections/TestedFeaturesSection/TestedFeaturesSection";

// Use z.custom() to accept the exact TypeScript types without Zod inferring different types
const OptionArraySchema = z.custom<Option[]>();
const PcmFileSchema = z.custom<PcmFileMetadata>();
const TestedFeaturesSchema = z
  .custom<FeatureData[]>()
  .refine(
    (features) => Array.isArray(features) && features.length > 0,
    "At least one tested feature is required",
  );

// Shared fields between maintenance and new product submissions
const PcmBaseFields = {
  scheme: createZodStringOption().refine(
    (v) => v.value !== "",
    "Please select a scheme",
  ),
  ccProduct: createZodStringOption().refine(
    (v) => v.value !== "",
    "Please select a product",
  ),
  ccLab: createZodNumberOption().refine(
    (v) => v.value !== 0,
    "Please select a lab",
  ),
  phone1: z.string(),
  custemail: z.email("Please enter a valid email address"),
  custuname: z.string().min(1, "Full name is required"),
  vendor: z.string(),
  additionalInformation: z.string(),
  testedFeatures: TestedFeaturesSchema,
  fileType: z.string(),
  fileLabel: z.string(),
  // Edit mode fields (only used when editing existing PCM)
  deadline: z.string(),
  update: z.string(),
  send_response: z.boolean(),
};

// Maintenance submission - requires ccMaintenance, no PP or file requirements
const MaintenanceSubmissionSchema = z.object({
  ...PcmBaseFields,
  isMaintenance: z.literal(true),
  ccMaintenance: createZodNumberOption().refine(
    (v) => v.value !== 0,
    "Please select a maintenance",
  ),
  protectionProfiles: OptionArraySchema,
  files: z.array(PcmFileSchema),
});

// New product submission - requires PPs and specific files, ccMaintenance optional
const NewProductSubmissionSchema = z.object({
  ...PcmBaseFields,
  isMaintenance: z.literal(false),
  ccMaintenance: createZodNumberOption(),
  protectionProfiles: OptionArraySchema.refine(
    (arr) => arr.length > 0,
    "At least one protection profile is required",
  ),
  files: z
    .array(PcmFileSchema)
    .refine(
      (files) =>
        files.some(
          (file) => file.fileType === "Administrative Guide" && file.active,
        ),
      "Administrative Guide (AGD) file is required",
    )
    .refine(
      (files) =>
        files.some(
          (file) =>
            file.fileType === "Assurance Activity Report" && file.active,
        ),
      "Assurance Activity Report (AAR) file is required",
    ),
});

// Main PCM form schema using discriminatedUnion on isMaintenance
export const PcmFormValidationSchema = z.discriminatedUnion("isMaintenance", [
  MaintenanceSubmissionSchema,
  NewProductSubmissionSchema,
]);

// Edit mode submission - no file requirements since files already exist on server
const EditSubmissionSchema = z.object({
  ...PcmBaseFields,
  isMaintenance: z.literal(false),
  ccMaintenance: createZodNumberOption(),
  protectionProfiles: OptionArraySchema.refine(
    (arr) => arr.length > 0,
    "At least one protection profile is required",
  ),
  files: z.array(PcmFileSchema),
});

// Edit mode schema - no file requirements
export const PcmFormValidationSchemaEdit = z.discriminatedUnion(
  "isMaintenance",
  [MaintenanceSubmissionSchema, EditSubmissionSchema],
);

export type PcmFormValues = z.infer<typeof PcmFormValidationSchema>;
